import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Send, RefreshCw, ShieldCheck, AlertTriangle, Server,
  Link2, Gauge, TrendingUp, Power, Clock,
} from 'lucide-react';
import {
  apiMailboxes, apiMailboxConnectUrl, apiMailboxDiscover, apiMailboxDisconnect,
  apiMailboxVerifyConnection, apiAddMailbox, apiUpdateMailbox, apiRemoveMailbox,
  apiTestMailbox, apiMailboxHealth,
} from '../../api';
import { Field, Toggle, ActionButton, Toast, useToast, Empty, formatDateTime } from './parts';
import CapacityMeter from './CapacityMeter';
import styles from './Mailer.module.css';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Asia/Karachi', 'Asia/Dubai', 'Asia/Kolkata',
  'Australia/Sydney', 'UTC',
];

const REGION_OPTIONS = [
  { value: 'com', label: 'Global — zoho.com' },
  { value: 'eu', label: 'Europe — zoho.eu' },
  { value: 'in', label: 'India — zoho.in' },
  { value: 'com.au', label: 'Australia — zoho.com.au' },
  { value: 'jp', label: 'Japan — zoho.jp' },
  { value: 'ca', label: 'Canada — zohocloud.ca' },
];

/** Why a mailbox is sitting idle, in words rather than a status code. */
const BLOCK_REASONS = {
  mailbox_disabled: 'Switched off',
  connection_expired: 'Its Zoho login needs reconnecting',
  connection_error: 'Its Zoho login is erroring',
  mailbox_limit_reached: 'Daily limit reached — resumes tomorrow',
  waiting_for_gap: 'Spacing out the next send',
  removed: 'Removed',
};

// `unknown` means the lookup timed out, not that the record is absent. It gets
// its own muted tone so it can never be misread as a verdict either way.
const AUTH_TONE = {
  pass: 'var(--success)',
  warn: 'var(--warning, #b7791f)',
  fail: 'var(--danger)',
  unknown: 'var(--text-muted)',
};

/**
 * Whether the chosen spacing can actually deliver the chosen daily limit.
 *
 * The gap is not derived from the limit, and it cannot be: the scheduler waits
 * a random interval after each send, so the two settings are independent and
 * can silently disagree. Too wide a gap and the day ends before the quota is
 * reached — the mailbox quietly under-delivers with no error anywhere. Too
 * narrow and the whole day's volume goes out in a burst before lunch, which is
 * the pattern the jitter exists to hide.
 *
 * Rather than compute one from the other and take the choice away, this states
 * the consequence in hours so the trade-off is visible while it is being made.
 */
const PacingHint = ({ dailyLimit, minGap, maxGap }) => {
  if (!Number.isFinite(dailyLimit) || !Number.isFinite(minGap) || !Number.isFinite(maxGap)) return null;
  if (dailyLimit <= 0 || minGap <= 0 || maxGap <= 0) return null;

  const averageGap = (Math.min(minGap, maxGap) + Math.max(minGap, maxGap)) / 2;
  // n sends need n-1 gaps between them.
  const hours = ((dailyLimit - 1) * averageGap) / 60;

  // Campaigns may send throughout an enabled calendar day. The old UI used an
  // eight-hour 9-to-5 yardstick even after that scheduler restriction was
  // removed, so it warned about a limit that no longer existed.
  const sendingDayHours = 24;
  const tooSlow = hours > sendingDayHours;
  const tooFast = hours < 2 && dailyLimit >= 10;

  return (
    <div className={tooSlow || tooFast ? styles.warnBox : styles.infoBox} style={{ marginBottom: 0 }}>
      <div className={styles.boxTitle}>
        <Clock size={15} />
        {dailyLimit} a day, one every {Math.round(averageGap)} min on average — about{' '}
        {hours < 1 ? 'under an hour' : `${hours.toFixed(1)} hours`} of sending
      </div>
      {tooSlow && (
        <>
          These gaps are too wide to fit {dailyLimit} into a full sending day. Only about{' '}
          {Math.floor((sendingDayHours * 60) / averageGap) + 1} would go out before the daily counter resets,
          and the rest roll to tomorrow. Lower the gaps to roughly{' '}
          <strong>{Math.max(1, Math.floor((sendingDayHours * 60) / dailyLimit) - 6)}</strong>–
          <strong>{Math.max(2, Math.ceil((sendingDayHours * 60) / dailyLimit) + 6)}</strong> minutes, or lower the daily limit.
        </>
      )}
      {tooFast && (
        <>
          That is the whole day's volume inside {hours.toFixed(1)} hours, then silence. A burst
          followed by nothing reads as automated. Widen the gaps to roughly{' '}
          <strong>{Math.max(1, Math.floor((sendingDayHours * 60) / dailyLimit) - 6)}</strong>–
          <strong>{Math.max(2, Math.ceil((sendingDayHours * 60) / dailyLimit) + 6)}</strong> minutes to spread it
          across the day.
        </>
      )}
      {!tooSlow && !tooFast && (
        <>
          That fits within the full sending day. The gap is re-drawn at random after every send,
          so no two intervals match — which is the point.
        </>
      )}
    </div>
  );
};

/**
 * One mailbox: its budget, its persona, and how much of it campaigns have taken.
 *
 * The row is the whole editing surface — no modal. Changing a daily limit is the
 * most frequent thing anyone does here, and making it a two-click dialog would
 * turn "give the new domain five a day while it warms up" into a chore.
 */
const MailboxRow = ({ mailbox, jwtToken, onChanged, report }) => {
  const [draft, setDraft] = useState(mailbox);
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [testTo, setTestTo] = useState('');

  useEffect(() => { setDraft(mailbox); }, [mailbox]);

  const patch = (changes) => setDraft((d) => ({ ...d, ...changes }));

  /**
   * A number field the user may have blanked while typing.
   *
   * An empty input gives `Number('') === 0` and a missing one gives `NaN`,
   * which serialises to `null`. Both would be posted straight into fields with
   * a `min: 1` validator — so a half-typed value must fall back to what the
   * mailbox already has rather than being sent as-is.
   */
  const numberOr = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const save = async (overrides = {}) => {
    const payload = {
      fromName: draft.fromName,
      dailyLimit: numberOr(draft.dailyLimit, mailbox.dailyLimit ?? 50),
      enabled: draft.enabled,
      minGapMinutes: numberOr(draft.minGapMinutes, 12),
      maxGapMinutes: numberOr(draft.maxGapMinutes, 35),
      timezone: draft.timezone || 'America/New_York',
      signatureHtml: draft.signatureHtml,
      warmup: draft.warmup,
      ...overrides,
    };
    // A minimum above the maximum would make the random draw collapse to a
    // single fixed interval — which is the machine fingerprint the jitter
    // exists to avoid. Swap rather than reject; the intent is obvious.
    if (payload.minGapMinutes > payload.maxGapMinutes) {
      [payload.minGapMinutes, payload.maxGapMinutes] = [payload.maxGapMinutes, payload.minGapMinutes];
    }
    const result = await apiUpdateMailbox(jwtToken, mailbox._id, payload);
    if (result.warning) report.warn(result.warning);
    else report.ok(`${mailbox.address} updated`);
    await onChanged();
  };

  const toggleEnabled = async () => {
    const next = !draft.enabled;
    patch({ enabled: next });
    try {
      await apiUpdateMailbox(jwtToken, mailbox._id, { enabled: next });
      report.ok(next ? `${mailbox.address} is sending again` : `${mailbox.address} paused — campaigns on it will wait`);
      await onChanged();
    } catch (e) {
      patch({ enabled: !next });
      report.fail(e);
      throw e;
    }
  };

  const runHealth = async () => {
    try {
      setHealth(await apiMailboxHealth(jwtToken, mailbox._id));
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const sendTest = async () => {
    try {
      const result = await apiTestMailbox(jwtToken, mailbox._id, { to: testTo || undefined });
      report.ok(`Test sent from ${result.from} to ${result.to}. Check which folder it landed in.`);
      await onChanged();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const remove = async () => {
    if (!window.confirm(
      `Remove ${mailbox.address}?\n\nIt stops sending and comes off every campaign using it. `
      + 'Sent history is kept, and contacts mid-sequence on this address are freed to '
      + 'continue from another one.'
    )) return;
    try {
      const result = await apiRemoveMailbox(jwtToken, mailbox._id);
      report.ok(
        result.campaignsUpdated?.length
          ? `Removed. Taken off: ${result.campaignsUpdated.join(', ')}.`
          : 'Mailbox removed.'
      );
      await onChanged();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const usedPct = mailbox.limitToday ? Math.min(100, (mailbox.sentToday / mailbox.limitToday) * 100) : 0;
  const allocPct = mailbox.limitToday ? Math.min(100, (mailbox.allocated / mailbox.limitToday) * 100) : 0;
  const unhealthy = !mailbox.enabled || mailbox.connectionStatus !== 'active' || mailbox.status === 'error';

  return (
    <div className={`${styles.mailboxCard} ${unhealthy ? styles.mailboxCardMuted : ''}`}>
      <div className={styles.mailboxHead}>
        <button
          className={styles.mailboxIdentity}
          onClick={() => setOpen((v) => !v)}
          type="button"
          aria-expanded={open}
        >
          <div className={styles.mailboxAddress}>
            {mailbox.address}
            {mailbox.warmupActive && (
              <span className={styles.chipWarm} title="Warming up — the ceiling climbs daily">
                <TrendingUp size={11} /> warming
              </span>
            )}
            {!mailbox.enabled && <span className={styles.chipOff}>paused</span>}
          </div>
          <div className={styles.mailboxSub}>
            {mailbox.fromName ? `${mailbox.fromName} · ` : ''}{mailbox.domain}
          </div>
        </button>

        <div className={styles.mailboxNumbers}>
          <div className={styles.mailboxNumber}>
            <strong>{mailbox.sentToday}</strong>
            <span>sent today</span>
          </div>
          <div className={styles.mailboxNumber}>
            <strong>{mailbox.limitToday}</strong>
            <span>daily limit</span>
          </div>
          <div className={styles.mailboxNumber}>
            <strong className={mailbox.overAllocated ? styles.numberDanger : undefined}>
              {mailbox.unallocated}
            </strong>
            <span>free to give</span>
          </div>
        </div>

        <ActionButton
          className="btn btn-ghost btn-sm"
          onClick={toggleEnabled}
          icon={<Power size={15} />}
          busyLabel="…"
          doneLabel={draft.enabled ? 'On' : 'Off'}
          failLabel="Failed"
          title={draft.enabled ? 'Pause this mailbox' : 'Resume this mailbox'}
        >
          {draft.enabled ? 'On' : 'Off'}
        </ActionButton>
      </div>

      {/* Two bars on one track: how much is claimed, and how much is spent. The
          gap between them is today's unused-but-promised capacity, which is the
          thing you actually want to see at a glance. */}
      <div className={styles.usageTrack} title={`${mailbox.sentToday} sent · ${mailbox.allocated} claimed by campaigns · ${mailbox.limitToday} limit`}>
        <div className={styles.usageAllocated} style={{ width: `${allocPct}%` }} />
        <div className={styles.usageSent} style={{ width: `${usedPct}%` }} />
      </div>

      <div className={styles.mailboxFooter}>
        {mailbox.blockedBy && (
          <span className={styles.mailboxNote}>
            <AlertTriangle size={13} /> {BLOCK_REASONS[mailbox.blockedBy] || mailbox.blockedBy}
          </span>
        )}
        {mailbox.overAllocated > 0 && (
          <span className={styles.mailboxNoteDanger}>
            <AlertTriangle size={13} /> Campaigns claim {mailbox.allocated}/day but the limit is {mailbox.limitToday}
          </span>
        )}
        {mailbox.lastError && (
          <span className={styles.mailboxNoteDanger} title={mailbox.lastError}>
            <AlertTriangle size={13} /> {mailbox.lastError.slice(0, 90)}
          </span>
        )}
        {mailbox.campaigns?.length > 0 && (
          <span className={styles.mailboxNote}>
            {mailbox.campaigns.map((c) => `${c.campaign} (${c.dailyCap}/day)`).join(' · ')}
          </span>
        )}
        {!mailbox.campaigns?.length && !mailbox.blockedBy && (
          <span className={styles.mailboxNote}>No campaign is using this mailbox yet</span>
        )}
      </div>

      {open && (
        <div className={styles.mailboxBody}>
          <div className={styles.fieldRow}>
            <Field
              label="Daily limit"
              help="The hard ceiling for this address. Nothing sends past it, however many campaigns point here."
            >
              <input
                className="input" type="number" min="0" max="500"
                value={draft.dailyLimit}
                onChange={(e) => patch({ dailyLimit: e.target.value })}
              />
            </Field>
            <Field label="From name" help="A real person's name outperforms a company name.">
              <input
                className="input"
                value={draft.fromName || ''}
                onChange={(e) => patch({ fromName: e.target.value })}
                placeholder="Mustafa at Evercrest"
              />
            </Field>
            <Field
              label="Minimum gap (min)"
              help="Random spacing between this mailbox's sends. A fixed interval is a machine fingerprint."
            >
              <input
                className="input" type="number" min="1"
                value={draft.minGapMinutes ?? ''}
                onChange={(e) => patch({ minGapMinutes: e.target.value })}
              />
            </Field>
            <Field label="Maximum gap (min)">
              <input
                className="input" type="number" min="1"
                value={draft.maxGapMinutes ?? ''}
                onChange={(e) => patch({ maxGapMinutes: e.target.value })}
              />
            </Field>
            <Field label="Timezone" help="When this mailbox's daily counter resets.">
              <select
                className="input"
                value={draft.timezone}
                onChange={(e) => patch({ timezone: e.target.value })}
              >
                {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <PacingHint
            dailyLimit={Number(draft.dailyLimit)}
            minGap={Number(draft.minGapMinutes)}
            maxGap={Number(draft.maxGapMinutes)}
          />

          <Field
            label="Signature"
            help="Overrides the global signature for this address. Keep it plain text — logos and social icons make an email read as marketing."
          >
            <textarea
              className="input"
              style={{ minHeight: 60, resize: 'vertical' }}
              value={draft.signatureHtml || ''}
              onChange={(e) => patch({ signatureHtml: e.target.value })}
              placeholder="(uses the global signature)"
            />
          </Field>

          <Toggle
            label="Warm this domain up"
            help={
              `Starts at ${draft.warmup?.startLimit ?? 5}/day and adds ${draft.warmup?.incrementPerDay ?? 3}/day `
              + `until it reaches the limit above. A domain with no sending history going straight to full volume `
              + `is the classic pattern filters look for — worth doing on every newly bought domain.`
            }
            checked={!!draft.warmup?.enabled}
            onChange={(v) => patch({ warmup: { ...draft.warmup, enabled: v } })}
          />

          {draft.warmup?.enabled && (
            <div className={styles.fieldRow}>
              <Field label="Start at">
                <input
                  className="input" type="number" min="1"
                  value={draft.warmup.startLimit ?? 5}
                  onChange={(e) => patch({ warmup: { ...draft.warmup, startLimit: Number(e.target.value) } })}
                />
              </Field>
              <Field label="Add per day">
                <input
                  className="input" type="number" min="1"
                  value={draft.warmup.incrementPerDay ?? 3}
                  onChange={(e) => patch({ warmup: { ...draft.warmup, incrementPerDay: Number(e.target.value) } })}
                />
              </Field>
            </div>
          )}

          {health && (
            <div className={health.verdict === 'pass' ? styles.infoBox : styles.warnBox}>
              <div className={styles.boxTitle}>
                <ShieldCheck size={15} /> {health.domain} — {health.summary}
              </div>
              <div className={styles.authRow}>
                {['spf', 'dkim', 'dmarc', 'mx'].map((key) => (
                  <span key={key} style={{ color: AUTH_TONE[health.checks[key]?.status] }}>
                    {key.toUpperCase()} {health.checks[key]?.status}
                  </span>
                ))}
              </div>
              {Object.values(health.checks).filter((c) => c.fix).map((c) => (
                <div key={c.fix} className={styles.checkFix}>{c.fix}</div>
              ))}
            </div>
          )}

          <div className={styles.toolbar} style={{ marginBottom: 0, marginTop: 4 }}>
            <ActionButton
              className="btn btn-primary" onClick={() => save()}
              busyLabel="Saving…" doneLabel="Saved" failLabel="Not saved"
            >
              Save
            </ActionButton>
            <ActionButton
              className="btn btn-ghost" onClick={runHealth} icon={<ShieldCheck size={15} />}
              busyLabel="Checking DNS…" doneLabel="Checked" failLabel="Check failed"
            >
              Check SPF / DKIM / DMARC
            </ActionButton>
            <input
              className="input"
              style={{ maxWidth: 200 }}
              placeholder="you@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <ActionButton
              className="btn btn-ghost" onClick={sendTest} icon={<Send size={15} />}
              busyLabel="Sending…" doneLabel="Sent" failLabel="Send failed"
            >
              Send a test
            </ActionButton>
            <div className={styles.spacer} />
            <ActionButton
              className="btn btn-ghost" onClick={remove} icon={<Trash2 size={15} />}
              busyLabel="Removing…" doneLabel="Removed" failLabel="Not removed"
              style={{ color: 'var(--danger)' }}
            >
              Remove
            </ActionButton>
          </div>

          <p className="text-sm text-muted" style={{ margin: '10px 0 0', lineHeight: 1.6 }}>
            {mailbox.lastSentAt ? <>Last sent {formatDateTime(mailbox.lastSentAt)}. </> : null}
            Send a test, then open it and use “Show original” in Gmail: the headers should read{' '}
            <code>dkim=pass spf=pass dmarc=pass</code>. That is what decides whether this domain
            reaches inboxes at all.
          </p>
        </div>
      )}
    </div>
  );
};

/** A connected Zoho login and everything under it. */
const ConnectionCard = ({ connection, mailboxes, jwtToken, onChanged, report }) => {
  const [candidates, setCandidates] = useState(null);

  const discover = async () => {
    try {
      const { addresses } = await apiMailboxDiscover(jwtToken, connection._id);
      setCandidates(addresses);
      const fresh = addresses.filter((a) => !a.alreadyAdded).length;
      report.ok(fresh ? `${fresh} address(es) not yet set up` : 'Every address on this login is already set up');
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const add = async (candidate) => {
    try {
      await apiAddMailbox(jwtToken, {
        connectionId: connection._id,
        address: candidate.address,
        providerAccountId: candidate.providerAccountId,
        fromName: candidate.displayName,
      });
      report.ok(`${candidate.address} added`);
      setCandidates((list) => list.map((c) => (c.address === candidate.address ? { ...c, alreadyAdded: true } : c)));
      await onChanged();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const verify = async () => {
    try {
      const result = await apiMailboxVerifyConnection(jwtToken, connection._id);
      if (result.ok) report.ok(`"${connection.label}" is working`);
      else report.warn(result.error || 'This login is not working');
      await onChanged();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      `Disconnect "${connection.label}"?\n\nThe ${mailboxes.length} mailbox(es) under it stop sending `
      + 'immediately. Nothing is deleted — reconnecting brings them back.'
    )) return;
    try {
      const result = await apiMailboxDisconnect(jwtToken, connection._id);
      report.ok(`Disconnected. ${result.mailboxesDisabled} mailbox(es) paused.`);
      await onChanged();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const broken = connection.status !== 'active';

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className={styles.connectionHead}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="card-title" style={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server size={16} /> {connection.label || 'Zoho login'}
            <span className={broken ? styles.chipDanger : styles.chipOk}>{connection.status}</span>
          </h3>
          <p className="page-subtitle" style={{ margin: 0 }}>
            {mailboxes.length} mailbox{mailboxes.length === 1 ? '' : 'es'}
            {' · '}
            {[...new Set(mailboxes.map((m) => m.domain))].join(', ') || 'no addresses yet'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ActionButton
            className="btn btn-ghost btn-sm" onClick={verify} icon={<Link2 size={14} />}
            busyLabel="Checking…" doneLabel="Working" failLabel="Failed"
          >
            Check
          </ActionButton>
          <ActionButton
            className="btn btn-ghost btn-sm" onClick={discover} icon={<RefreshCw size={14} />}
            busyLabel="Looking…" doneLabel="Done" failLabel="Failed"
            title="Re-read the addresses this login can send from"
          >
            Find addresses
          </ActionButton>
          <ActionButton
            className="btn btn-ghost btn-sm" onClick={disconnect}
            busyLabel="…" doneLabel="Disconnected" failLabel="Failed"
            style={{ color: 'var(--danger)' }}
          >
            Disconnect
          </ActionButton>
        </div>
      </div>

      {broken && (
        <div className={styles.dangerBox}>
          <div className={styles.boxTitle}>
            <AlertTriangle size={16} /> This login needs reconnecting
          </div>
          {connection.lastError || 'Its authorization is no longer valid.'} Every mailbox under it is
          stalled until you reconnect — use “Connect a mailbox” above and sign in to this same Zoho
          account. It will be matched back to this connection rather than duplicated.
        </div>
      )}

      {candidates && (
        <div className={styles.candidateList}>
          {candidates.length === 0 && <div className="text-sm text-muted">No sendable addresses found.</div>}
          {candidates.map((c) => (
            <div key={c.address} className={styles.candidateRow}>
              <span>{c.address}</span>
              {c.alreadyAdded ? (
                <span className="text-sm text-muted">already set up</span>
              ) : (
                <ActionButton
                  className="btn btn-ghost btn-sm" onClick={() => add(c)} icon={<Plus size={14} />}
                  busyLabel="Adding…" doneLabel="Added" failLabel="Failed"
                >
                  Add as a mailbox
                </ActionButton>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.mailboxList}>
        {mailboxes.map((mailbox) => (
          <MailboxRow
            key={mailbox._id}
            mailbox={mailbox}
            jwtToken={jwtToken}
            onChanged={onChanged}
            report={report}
          />
        ))}
        {mailboxes.length === 0 && (
          <div className="text-sm text-muted" style={{ padding: '8px 2px' }}>
            No mailboxes yet on this login — press “Find addresses”.
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Every domain you can send from, in one place.
 *
 * The screen answers three questions in order, because that is the order they
 * get asked: which logins are connected, how much can each address send a day,
 * and how much of that total is still unclaimed.
 */
const MailboxesTab = ({ jwtToken, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [region, setRegion] = useState('com');
  const [label, setLabel] = useState('');
  const [showConnect, setShowConnect] = useState(false);
  const report = useToast();

  const load = useCallback(async () => {
    try {
      setData(await apiMailboxes(jwtToken));
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load mailboxes');
    } finally {
      setLoading(false);
    }
  }, [jwtToken]);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  const connect = async () => {
    try {
      const { url } = await apiMailboxConnectUrl(jwtToken, region);
      // Zoho's redirect lands on /redirect with only a code. The label and
      // region are needed to finish the exchange, and Zoho's `state` parameter
      // is not reliably passed through every regional variant — so they are
      // parked here and picked up on the way back.
      sessionStorage.setItem('mailbox_connect', JSON.stringify({ region, label }));
      window.location.href = url;
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 30, height: 30 }} /></div>;
  if (error) return <div className={styles.dangerBox}>{error}</div>;

  const { connections = [], mailboxes = [], totals = {} } = data || {};
  const byConnection = new Map();
  mailboxes.forEach((m) => {
    const key = String(m.connection);
    if (!byConnection.has(key)) byConnection.set(key, []);
    byConnection.get(key).push(m);
  });

  return (
    <div>
      <Toast message={report.toast?.message} tone={report.toast?.tone} onDismiss={report.clear} />

      <CapacityMeter totals={totals} mailboxes={mailboxes} />

      <div className={styles.toolbar}>
        <div className={styles.spacer} />
        <button className="btn btn-primary" onClick={() => setShowConnect((v) => !v)}>
          <Plus size={16} /> Connect a mailbox
        </button>
      </div>

      {showConnect && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 className="card-title" style={{ marginBottom: 4 }}>Connect a Zoho login</h3>
          <p className="page-subtitle" style={{ marginBottom: 14 }}>
            One login per Zoho account. If all your domains sit under a single Zoho account, connect
            it once and every address appears below. If you bought each domain on its own Zoho
            signup, connect each one — they stack up here as separate logins.
          </p>

          <div className={styles.fieldRow}>
            <Field label="Name it" help="Just for you — e.g. “Evercrest main” or “Outreach domain 2”.">
              <input
                className="input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Evercrest main"
              />
            </Field>
            <Field
              label="Zoho region"
              help="Must match where the account was created. A token from the wrong region is rejected outright."
            >
              <select className="input" value={region} onChange={(e) => setRegion(e.target.value)}>
                {REGION_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
          </div>

          <div className={styles.toolbar} style={{ marginBottom: 0, marginTop: 12 }}>
            <ActionButton
              className="btn btn-primary" onClick={connect} icon={<Link2 size={16} />}
              busyLabel="Redirecting…" doneLabel="Redirecting" failLabel="Could not start"
            >
              Continue to Zoho
            </ActionButton>
            <button className="btn btn-ghost" onClick={() => setShowConnect(false)}>Cancel</button>
          </div>

          <p className="text-sm text-muted" style={{ marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
            Reconnecting an account you already have is safe: it is matched by the addresses it
            carries and updated in place, not duplicated.
          </p>
        </div>
      )}

      {connections.length === 0 ? (
        <Empty icon={<Server size={40} color="var(--text-muted)" />} title="No mailboxes connected">
          Connect the Zoho account that owns your sending domains. Every address it can send from
          becomes a mailbox here, with its own daily limit that campaigns draw against.
        </Empty>
      ) : (
        connections.map((connection) => (
          <ConnectionCard
            key={connection._id}
            connection={connection}
            mailboxes={byConnection.get(String(connection._id)) || []}
            jwtToken={jwtToken}
            onChanged={refresh}
            report={report}
          />
        ))
      )}

      {mailboxes.length > 0 && (
        <div className={styles.infoBox}>
          <div className={styles.boxTitle}><Gauge size={16} /> How the limits fit together</div>
          Each mailbox has a hard daily limit — nothing sends past it, however many campaigns point
          at it. A campaign then claims a share of specific mailboxes: “20 a day from each of these
          three” is 60 a day for that campaign, and leaves{' '}
          <strong>{totals.unallocated}</strong> of your {totals.dailyLimit} still free for the next one.
          Follow-ups always come back from the address that started the conversation.
        </div>
      )}
    </div>
  );
};

export default MailboxesTab;
